import React from 'react';

import type { CardId } from '@vlabs/api/point-api/model';
import { useCardListLinker } from '@vlabs/api/useCardListLinker';
import { CrossCircleIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { confirmDetachCardFromList } from './confirm-detach-card-from-list';
import type { DetachCardsFromListProps } from './types';

interface DetachCardsFromListIconButtonProps extends
  Omit<DetachCardsFromListProps, 'cardIds'>,
  IconButtonProps {
  cardId: CardId;
}

const displayName = 'DetachCardsFromListIconButton';
const testid = buildTestid(displayName);

export function DetachCardsFromListIconButton({
  cardId,
  fromListId: listId,
  onDetachCardsFromList = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: DetachCardsFromListIconButtonProps): React.JSX.Element {
  const { t } = useTranslation();

  const detachCards = useCardListLinker(({
    action: 'detach',
    cardIds: [cardId],
    listId,
    onSuccess: () => {
      onDetachCardsFromList?.();
      toast.success(t('features:detachCardsFromList.toast_success'));
    },
  }));

  const onClickHandler = () => {
    confirmDetachCardFromList({
      listId,
      onConfirm: detachCards,
    });
  };

  return (
    <Control.IconButton
      icon={<CrossCircleIcon />}
      kind="negative"
      onClick={onClickHandler}
      variant="flat"
      {...testid(dataTestId, listId)}
      {...props}
    />
  );
}
